'use client'

import {
  AuthErrorMessage,
  AuthSubmitButton,
  AuthTextField,
} from '@/features/auth/AuthFormControls'
import { AuthSplitLayout } from '@/features/auth/AuthPageLayout'
import SocialProviderChooser from '@/features/auth/SocialProviderChooser'
import useSocialAuthentication from '@/features/auth/useSocialAuthentication'
import { GetSignupOptions, SignUpUser } from '@/lib/apis/authentication'
import { loginWithPostHogSession } from '@/lib/apis/authentication-client'
import { translateError } from '@/lib/apis/error'
import useApiClient from '@/lib/apis/clients/useApiClient'
import { SetPassword } from '@/lib/apis/profile'
import type { AuthenticationMethod, SocialProvider } from '@/lib/apis/types'
import {
  socialAuthenticationErrorMessage,
  socialProviderLabel,
  SOCIAL_PROVIDER_ORDER,
} from '@/lib/auth/social-auth'
import { useMutation } from '@tanstack/react-query'
import { Form, Formik } from 'formik'
import Link from 'next/link'
import { useState } from 'react'
import { object, string } from 'yup'

type Props = {
  redirect: string
  socialError?: string
}

type Stage =
  | { type: 'email' }
  | { type: 'new-password'; email: string }
  | {
      type: 'social-only'
      email: string
      continuationToken: string
      methods: AuthenticationMethod[]
    }
  | {
      type: 'set-password'
      email: string
      proofToken: string
      provider: SocialProvider
    }

const POST_SIGN_UP_LOGIN_RECURS = 5
const POST_SIGN_UP_LOGIN_INITIAL_DELAY_MS = 100
const POST_SIGN_UP_LOGIN_ERROR_MESSAGE =
  "Your account was created, but we couldn't log you in. Please log in with the same email and password you just used. If that doesn't work, contact support@nextclinic.com.au."

const emailValidator = object({
  email: string()
    .default('')
    .required('Email address is required')
    .email('Enter a valid email address'),
})

const passwordValidator = object({
  password: string()
    .default('')
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters long'),
  password2: string()
    .default('')
    .test(
      'newPasswordsMustMatch',
      'The passwords must be the same',
      function () {
        return this.parent.password === this.parent.password2
      },
    ),
})

export default function SignUpForm({ redirect, socialError }: Props) {
  const [stage, setStage] = useState<Stage>({ type: 'email' })
  const [message, setMessage] = useState(
    socialError ? socialAuthenticationErrorMessage(socialError) : '',
  )
  const api = useApiClient()
  const signupOptionsMutation = useMutation(GetSignupOptions.mutation(api))
  const signUpMutation = useMutation(SignUpUser.mutation(api))
  const setPasswordMutation = useMutation(SetPassword.mutation(api))
  const socialAuthentication = useSocialAuthentication()

  async function loginAfterSignUp(username: string, password: string) {
    let retryDelayMs = POST_SIGN_UP_LOGIN_INITIAL_DELAY_MS

    for (let attempt = 0; attempt <= POST_SIGN_UP_LOGIN_RECURS; attempt++) {
      const formData = new FormData()
      formData.append('username', username)
      formData.append('password', password)
      formData.append('redirect', 'true')
      formData.append('redirectTo', redirect)

      const error = await loginWithPostHogSession(formData)
      if (!error) return true

      if (attempt < POST_SIGN_UP_LOGIN_RECURS) {
        await new Promise((resolve) => setTimeout(resolve, retryDelayMs))
        retryDelayMs *= 2
      }
    }
    return false
  }

  async function inspectEmail(email: string) {
    setMessage('')
    const result = await signupOptionsMutation.mutateAsync({ email })
    switch (result.availability) {
      case 'NEW_ACCOUNT':
        setStage({ type: 'new-password', email })
        return
      case 'SOCIAL_ONLY_ACCOUNT':
        setStage({
          type: 'social-only',
          email,
          continuationToken: result.continuationToken,
          methods: result.methods,
        })
        return
      case 'PASSWORD_ACCOUNT':
        setMessage('There’s already an account with this email.')
        return
      case 'ROLE_CONFLICT':
        setMessage(
          'This email belongs to a non-patient account. Social sign-in is only available for patients.',
        )
    }
  }

  async function authenticateSocialOnly(
    provider: SocialProvider,
    currentStage: Extract<Stage, { type: 'social-only' }>,
  ) {
    const result = await socialAuthentication.start(provider, {
      popup: true,
      continuationToken: currentStage.continuationToken,
    })
    if (result?.ok && result.proofToken) {
      setMessage('')
      setStage({
        type: 'set-password',
        email: currentStage.email,
        proofToken: result.proofToken,
        provider,
      })
    } else if (result?.ok) {
      setMessage(
        'We confirmed your account but could not start password setup. Please try again.',
      )
    }
  }

  const socialMessage = socialAuthentication.error
    ? socialAuthenticationErrorMessage(socialAuthentication.error)
    : ''

  return (
    <AuthSplitLayout
      illustrationTitle="Real doctors, 6am to midnight."
      illustrationBody="Certificates, scripts and consults — without the waiting room."
    >
      <h1 className="text-2xl font-bold leading-[30px] text-[#0b0b0f] lg:text-[26px] lg:leading-[33px] lg:tracking-[-0.01em]">
        {stage.type === 'set-password'
          ? 'Set your password'
          : 'Create your account'}
      </h1>

      {stage.type === 'email' && (
        <Formik
          initialValues={emailValidator.cast({})}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              await inspectEmail(values.email)
            } catch (error) {
              setMessage(translateError(error))
            } finally {
              setSubmitting(false)
            }
          }}
          validationSchema={emailValidator}
        >
          {(props) => (
            <>
              <Form className="mt-6 flex flex-col lg:mt-5">
                <AuthTextField
                  autoComplete="email"
                  hasError={Boolean(message || socialMessage)}
                  label="Email"
                  name="email"
                  placeholder="you@email.com"
                  type="email"
                />
                {(message || socialMessage) && (
                  <AuthErrorMessage
                    action={
                      message.includes('already') ? (
                        <Link href="/login" className="hover:underline">
                          Sign in →
                        </Link>
                      ) : undefined
                    }
                    className="mt-2"
                  >
                    {message || socialMessage}
                  </AuthErrorMessage>
                )}
                <AuthSubmitButton className="mt-6" loading={props.isSubmitting}>
                  Continue
                </AuthSubmitButton>
              </Form>

              <SocialProviderChooser
                disabled={props.isSubmitting}
                onSelect={(provider) =>
                  socialAuthentication.start(provider, {
                    returnTo: redirect,
                    errorTo: '/signup',
                  })
                }
                pendingProvider={socialAuthentication.pendingProvider}
              />
            </>
          )}
        </Formik>
      )}

      {stage.type === 'new-password' && (
        <Formik
          initialValues={passwordValidator.cast({})}
          onSubmit={async (values, { setSubmitting }) => {
            setMessage('')
            try {
              await signUpMutation.mutateAsync({
                username: stage.email,
                password: values.password,
              })
              if (!(await loginAfterSignUp(stage.email, values.password))) {
                setMessage(POST_SIGN_UP_LOGIN_ERROR_MESSAGE)
                setSubmitting(false)
              }
            } catch (error) {
              setMessage(translateError(error))
              setSubmitting(false)
            }
          }}
          validationSchema={passwordValidator}
        >
          {(props) => (
            <Form className="mt-6 flex flex-col lg:mt-5">
              <p className="mb-5 text-[13.5px] leading-[19px] text-[#5C5C64]">
                Creating an account for <strong>{stage.email}</strong>
              </p>
              <AuthTextField
                autoComplete="new-password"
                description="At least 8 characters."
                label="Password"
                name="password"
                type="password"
              />
              <AuthTextField
                autoComplete="new-password"
                className="mt-[18px] lg:mt-5"
                label="Confirm password"
                name="password2"
                type="password"
              />
              {message && (
                <AuthErrorMessage className="mt-5">{message}</AuthErrorMessage>
              )}
              <AuthSubmitButton className="mt-6" loading={props.isSubmitting}>
                Create account
              </AuthSubmitButton>
              <BackButton onClick={() => setStage({ type: 'email' })} />
            </Form>
          )}
        </Formik>
      )}

      {stage.type === 'social-only' && (
        <div className="mt-6 lg:mt-5">
          <p className="text-[13.5px] leading-[19px] text-[#5C5C64]">
            An account already uses <strong>{stage.email}</strong>. Confirm it
            with one of its linked sign-in methods, then choose a password.
          </p>
          {(message || socialMessage) && (
            <AuthErrorMessage className="mt-4">
              {message || socialMessage}
            </AuthErrorMessage>
          )}
          <div className="mt-5">
            <SocialProviderChooser
              action="verify"
              onSelect={(provider) => authenticateSocialOnly(provider, stage)}
              pendingProvider={socialAuthentication.pendingProvider}
              providers={linkedProviders(stage.methods)}
              showDivider={false}
            />
          </div>
          <BackButton onClick={() => setStage({ type: 'email' })} />
        </div>
      )}

      {stage.type === 'set-password' && (
        <Formik
          initialValues={passwordValidator.cast({})}
          onSubmit={async (values, { setSubmitting }) => {
            setMessage('')
            try {
              await setPasswordMutation.mutateAsync({
                newPassword: values.password,
                email: stage.email,
                proofToken: stage.proofToken,
              })
              window.location.assign(redirect)
            } catch (error) {
              setMessage(translateError(error))
              setSubmitting(false)
            }
          }}
          validationSchema={passwordValidator}
        >
          {(props) => (
            <Form className="mt-6 flex flex-col lg:mt-5">
              <p className="mb-5 text-[13.5px] leading-[19px] text-[#5C5C64]">
                {stage.email} was confirmed with{' '}
                {socialProviderLabel(stage.provider)}. Add a password so you can
                also sign in by email.
              </p>
              <AuthTextField
                autoComplete="new-password"
                description="At least 8 characters."
                label="Password"
                name="password"
                type="password"
              />
              <AuthTextField
                autoComplete="new-password"
                className="mt-[18px] lg:mt-5"
                label="Confirm password"
                name="password2"
                type="password"
              />
              {message && (
                <AuthErrorMessage className="mt-5">{message}</AuthErrorMessage>
              )}
              <AuthSubmitButton className="mt-6" loading={props.isSubmitting}>
                Set password
              </AuthSubmitButton>
            </Form>
          )}
        </Formik>
      )}

      {stage.type !== 'set-password' && (
        <p className="mt-5 flex items-center justify-center gap-1.5 text-[13.5px] leading-[18px] text-[#82828b] lg:gap-[5px]">
          Already have an account?
          <Link
            className="font-semibold text-[#2563eb] hover:underline"
            href="/login"
          >
            Sign in
          </Link>
        </p>
      )}
    </AuthSplitLayout>
  )
}

function linkedProviders(methods: AuthenticationMethod[]) {
  return SOCIAL_PROVIDER_ORDER.filter((provider) =>
    methods.some(
      (method) =>
        method.type === provider && method.linked && method.configured,
    ),
  )
}

function BackButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      className="mt-4 self-center text-[13.5px] font-semibold text-[#2563EB] hover:underline"
      onClick={onClick}
      type="button"
    >
      ← Use another email
    </button>
  )
}
